"use client";

import { Container } from "@/components/ui/container";
import { TitlePage } from "@/components/ui/title-page";
import { OfferIllustration } from "./_components/offer-illustration";
import Link from "next/link";
import { motion, type Variants } from "framer-motion";
import { Icon } from "@/components/ui/icon";
import { Alert02Icon, Building03Icon, Call02Icon, CheckmarkBadge01Icon, CloudIcon, Comment01Icon, CreditCardIcon, File01Icon, Location01Icon, Mail01Icon, ShieldUserIcon, ShoppingCart01Icon, StarIcon, TruckDeliveryIcon, WeightScaleIcon } from "@hugeicons/core-free-icons";

const sections = [
  {
    icon: File01Icon,
    number: "1",
    title: "Термины и определения",
    items: [
      "Исполнитель — ИП / ООО Okey Food, оказывающий услуги по приготовлению и доставке рационов питания.",
      "Заказчик — физическое лицо, акцептовавшее настоящую Оферту и оформившее Заказ.",
      "Рацион питания — комплект готовых блюд с определённой суточной калорийностью (ккал), приготовленных Исполнителем и доставляемых Заказчику.",
      "Заказ — оформленная заявка Заказчика на оказание услуг с указанием рациона, периода доставки и адреса.",
      "Акцепт — полное и безоговорочное принятие Заказчиком условий настоящей Оферты путём совершения действий, указанных в разделе 3.",
    ],
  },
  {
    icon: StarIcon,
    number: "2",
    title: "Предмет договора",
    items: [
      "Исполнитель обязуется по заявке Заказчика приготовить рацион питания в соответствии с выбранной программой и ежедневно доставлять его по адресу Заказчика в согласованный временной интервал.",
      "Заказчик обязуется принять и оплатить оказанные услуги в порядке, предусмотренном настоящим Договором.",
      "Рационы питания включают несколько приёмов пищи в сутки (завтрак, обед, ужин и перекусы в зависимости от выбранной программы) с суммарной калорийностью согласно выбранному плану.",
      "Состав меню может изменяться Исполнителем в пределах согласованной калорийности и пищевой ценности без дополнительного согласования.",
    ],
  },
  {
    icon: ShoppingCart01Icon,
    number: "3",
    title: "Порядок оформления заказа. Акцепт оферты",
    items: [
      "Настоящий Договор является публичной офертой в соответствии со ст. 407 Гражданского кодекса Республики Беларусь.",
      "Полным и безоговорочным акцептом признаётся: оформление заказа на сайте okey-food.store через форму заказа; оформление заказа по телефону +375 (33) 623-78-85 с устным согласованием параметров; оформление заказа в мессенджере с согласованием рациона, периода и адреса доставки.",
      "Договор считается заключённым с момента направления Исполнителем подтверждения заказа (SMS, мессенджер или e-mail).",
      "Подтверждение направляется в течение 3 (трёх) часов с момента получения заявки и содержит: итоговую стоимость, период доставки и параметры заказа.",
      "Заказчик вправе отказаться от заказа без штрафных санкций не позднее 18:00 дня, предшествующего первой доставке.",
    ],
  },
  {
    icon: CreditCardIcon,
    number: "4",
    title: "Стоимость услуг и порядок оплаты",
    items: [
      "Стоимость услуг устанавливается в соответствии с действующим прейскурантом на сайте okey-food.store либо согласовывается индивидуально с менеджером Исполнителя.",
      "Стоимость указывается в белорусских рублях (BYN).",
      "Цена считается окончательно согласованной с момента направления Исполнителем подтверждения заказа. Исполнитель не вправе изменять стоимость услуг в одностороннем порядке после направления такого подтверждения.",
      "Оплата производится одним из согласованных способов: наличными курьеру при получении; безналичным переводом на расчётный счёт Исполнителя; иным способом по согласованию с менеджером.",
      "Датой оплаты считается дата поступления денежных средств на расчётный счёт Исполнителя или дата получения наличных курьером.",
    ],
  },
  {
    icon: TruckDeliveryIcon,
    number: "5",
    title: "Условия доставки",
    items: [
      "Доставка рационов осуществляется ежедневно с 19:00 до 23:00 по адресу, указанному Заказчиком при оформлении заказа.",
      "Территория доставки: г. Минск и ближайшие пригороды. Возможность доставки по конкретному адресу уточняется у менеджера.",
      "Стоимость доставки включена в стоимость рациона, если иное не оговорено при оформлении заказа.",
      "Обязательным условием доставки является присутствие Заказчика или уполномоченного им лица по адресу доставки в указанный временной интервал.",
      "В случае отсутствия Заказчика по адресу в согласованное время рацион считается доставленным надлежащим образом. Расходы по повторной доставке несёт Заказчик.",
      "Об изменении адреса доставки Заказчик обязан уведомить Исполнителя не позднее 12:00 дня доставки.",
    ],
  },
  {
    icon: CheckmarkBadge01Icon,
    number: "6",
    title: "Права и обязанности сторон",
    items: [
      "Исполнитель обязан: приготовить и доставить рацион в соответствии с условиями Договора; обеспечить соответствие рационов санитарно-гигиеническим требованиям и нормам безопасности пищевых продуктов; предоставить достоверную информацию о составе, калорийности и условиях хранения.",
      "Исполнитель вправе: изменять меню в рамках согласованной программы с сохранением калорийности; приостанавливать исполнение при наличии задолженности Заказчика; изменять условия Оферты с уведомлением Заказчика не менее чем за 3 дня.",
      "Заказчик обязан: своевременно и в полном объёме оплачивать услуги; обеспечить присутствие по адресу доставки или уполномочить иное лицо; незамедлительно уведомлять об изменении адреса доставки не позднее 12:00 дня доставки; самостоятельно оценить применимость рациона с учётом состояния здоровья и при наличии противопоказаний проконсультироваться с врачом.",
      "Заказчик вправе: получать полную информацию о составе и калорийности рационов; приостановить или прекратить доставку с уведомлением Исполнителя в порядке, предусмотренном разделом 10; предъявлять претензии по качеству в порядке, предусмотренном разделом 7.",
    ],
  },
  {
    icon: ShieldUserIcon,
    number: "7",
    title: "Качество. Претензии",
    items: [
      "Исполнитель гарантирует соответствие рационов питания санитарно-гигиеническим требованиям и нормам безопасности пищевых продуктов, установленным законодательством Республики Беларусь.",
      "Условия хранения блюд после получения: температурный режим +2…+6 °C, срок употребления — не позднее времени, указанного на упаковке.",
      "Продовольственные товары надлежащего качества возврату и обмену не подлежат в соответствии с законодательством Республики Беларусь о защите прав потребителей.",
      "В случае обнаружения несоответствия рациона по качеству Заказчик обязан: зафиксировать факт фотографически или видео; направить претензию на e-mail okeygood@gmail.com или по телефону не позднее 2 (двух) часов с момента получения.",
      "При обоснованной претензии Исполнитель по выбору Заказчика заменяет рацион, предоставляет скидку на следующий заказ или возвращает стоимость за несоответствующий день доставки. Претензия рассматривается в течение 7 (семи) рабочих дней.",
    ],
  },
  {
    icon: WeightScaleIcon,
    number: "8",
    title: "Ответственность сторон",
    items: [
      "Стороны несут ответственность за неисполнение или ненадлежащее исполнение своих обязательств в соответствии с законодательством Республики Беларусь.",
      "Исполнитель не несёт ответственности за: последствия употребления рациона при наличии у Заказчика медицинских противопоказаний, о которых Исполнитель не был уведомлён; невозможность доставки по причине отсутствия Заказчика или предоставления недостоверного адреса; ухудшение качества рациона вследствие несоблюдения Заказчиком условий хранения после передачи.",
      "В случае просрочки доставки по вине Исполнителя более чем на 2 (два) часа Заказчик вправе потребовать возврата стоимости соответствующего дня доставки.",
      "Общий размер ответственности Исполнителя ограничен стоимостью конкретного заказа, в рамках которого возникло нарушение, если иное не установлено законодательством.",
    ],
  },
  {
    icon: CloudIcon,
    number: "9",
    title: "Обстоятельства непреодолимой силы (форс-мажор)",
    items: [
      "Стороны освобождаются от ответственности за неисполнение обязательств, если оно вызвано обстоятельствами непреодолимой силы: стихийные бедствия, пожары, наводнения, эпидемии, военные действия, акты государственных органов, препятствующие исполнению (ст. 372 ГК РБ).",
      "Сторона, для которой наступили такие обстоятельства, обязана уведомить другую Сторону не позднее 3 (трёх) календарных дней с момента их наступления.",
      "Срок исполнения обязательств продлевается соразмерно периоду действия форс-мажора.",
      "Если обстоятельства непреодолимой силы продолжаются более 30 (тридцати) дней, каждая из Сторон вправе отказаться от Договора без возмещения убытков, за исключением обязательства по возврату уплаченных денежных средств за неоказанные услуги.",
    ],
  },
  {
    icon: Alert02Icon,
    number: "10",
    title: "Порядок изменения и расторжения договора",
    items: [
      "Исполнитель вправе в одностороннем порядке изменить условия настоящей Оферты, уведомив Заказчика не менее чем за 3 (три) дня путём публикации новой редакции на сайте или направления уведомления в мессенджер.",
      "Заказчик вправе отказаться от дальнейшего исполнения Договора, уведомив Исполнителя не позднее 18:00 дня, предшествующего следующей дате доставки. Оплата за уже приготовленные и доставленные рационы возврату не подлежит.",
      "Исполнитель вправе расторгнуть Договор в одностороннем порядке в случае систематического нарушения Заказчиком условий оплаты или иных существенных условий Договора, уведомив Заказчика не менее чем за 1 (один) день.",
    ],
  },
  {
    icon: Comment01Icon,
    number: "11",
    title: "Порядок разрешения споров",
    items: [
      "Все споры и разногласия разрешаются путём переговоров.",
      "При недостижении соглашения Заказчик вправе направить письменную претензию на e-mail okeygood@gmail.com. Исполнитель рассматривает претензию и направляет мотивированный ответ в течение 14 (четырнадцати) календарных дней.",
      "При недостижении соглашения в досудебном порядке спор передаётся на рассмотрение суда в соответствии с законодательством Республики Беларусь. Права потребителя — физического лица регулируются Законом РБ «О защите прав потребителей» № 90-З от 09.01.2002.",
      "Настоящий Договор регулируется законодательством Республики Беларусь.",
    ],
  },
];

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

export default function OfferPage() {
  return (
    <>
      <TitlePage
        title="Публичная оферта"
        description="Условия оказания услуг по приготовлению и доставке рационов питания по г. Минску. Оформляя заказ, вы принимаете настоящую оферту."
        updated="02.04.2026"
        illustration={<OfferIllustration className="h-[150px] w-[150px] md:h-[220px] md:w-[220px] lg:h-[260px] lg:w-[260px]" />}
      />

      <div className="bg-whitePrimary pb-20">
        <Container>
          <div className="mx-auto max-w-3xl space-y-6">

            {/* Реквизиты */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="rounded-2xl border border-colorPrimary/12 bg-white p-6 shadow-sm"
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-colorPrimary text-white shadow-sm">
                  <Icon icon={Building03Icon} className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-yellow-hover">Исполнитель</p>
                  <h2 className="mt-0.5 text-[17px] font-extrabold leading-snug text-colorPrimary">Реквизиты</h2>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-start gap-2.5 rounded-xl bg-whitePrimary p-3">
                  <Icon icon={Building03Icon} className="mt-0.5 h-4 w-4 shrink-0 text-greySecondary" />
                  <div>
                    <p className="text-xs text-greySecondary">Наименование</p>
                    <p className="text-sm font-medium text-colorPrimary">ИП / ООО Okey Food</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5 rounded-xl bg-whitePrimary p-3">
                  <Icon icon={Location01Icon} className="mt-0.5 h-4 w-4 shrink-0 text-greySecondary" />
                  <div>
                    <p className="text-xs text-greySecondary">Юридический адрес</p>
                    <p className="text-sm font-medium text-colorPrimary">г. Минск, пр. Дзержинского 10</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5 rounded-xl bg-whitePrimary p-3">
                  <Icon icon={Mail01Icon} className="mt-0.5 h-4 w-4 shrink-0 text-greySecondary" />
                  <div>
                    <p className="text-xs text-greySecondary">E-mail</p>
                    <Link href="mailto:okeygood@gmail.com" className="text-sm font-medium text-colorPrimary hover:underline underline-offset-2">
                      okeygood@gmail.com
                    </Link>
                  </div>
                </div>
                <div className="flex items-start gap-2.5 rounded-xl bg-whitePrimary p-3">
                  <Icon icon={Call02Icon} className="mt-0.5 h-4 w-4 shrink-0 text-greySecondary" />
                  <div>
                    <p className="text-xs text-greySecondary">Телефон</p>
                    <Link href="tel:+375336237885" className="text-sm font-medium text-colorPrimary hover:underline underline-offset-2">
                      +375 (33) 623-78-85
                    </Link>
                  </div>
                </div>
              </div>

              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200/60 px-4 py-3 text-xs leading-relaxed text-amber-700">
                <span className="font-semibold">Важно:</span> УНП, расчётный счёт и наименование банка необходимо уточнить и внести в данный раздел до публикации оферты.
              </div>
            </motion.div>

            {/* Sections */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-4"
            >
              {sections.map((section) => {
                
                return (
                  <motion.div
                    key={section.number}
                    variants={itemVariants}
                    className="group rounded-2xl border border-greySecondary/10 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md"
                  >
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-colorPrimary/8 text-colorPrimary transition-colors duration-200 group-hover:bg-colorPrimary group-hover:text-white">
                        <Icon icon={section.icon} className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-yellow-hover">
                          Раздел {section.number}
                        </p>
                        <h2 className="mt-0.5 text-[17px] font-extrabold leading-snug text-colorPrimary">{section.title}</h2>
                      </div>
                    </div>
                    <ul className="space-y-2.5">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-hover" />
                          <p className="text-sm leading-relaxed text-greySecondary">{item}</p>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Cross-link to privacy */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.45 }}
              className="flex items-center justify-between rounded-xl border border-greySecondary/10 bg-white px-5 py-3.5"
            >
              <p className="text-sm text-greySecondary">
                Ознакомьтесь также с нашей{" "}
                <Link href="/privacy" className="font-medium text-colorPrimary underline-offset-2 hover:underline">
                  Политикой конфиденциальности
                </Link>
              </p>
            </motion.div>

            {/* Footer note */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              className="flex flex-col gap-2 rounded-xl border border-greySecondary/10 bg-white px-5 py-4 text-sm text-greySecondary sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-center gap-2">
                <Icon icon={ShieldUserIcon} className="h-4 w-4 shrink-0 text-colorPrimary" />
                <span>Документ соответствует ГК РБ и Закону «О защите прав потребителей» № 90-З</span>
              </div>
              <span className="shrink-0 font-medium text-colorPrimary sm:pl-4">02.04.2026</span>
            </motion.div>

          </div>
        </Container>
      </div>
    </>
  );
}
